import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "video-player",
  class VideoPlayer extends WYATTElement {
    constructor() {
      super();

      this.nativeVideo = this.querySelector("video");
      this.externalVideo = this.querySelector("iframe");
      this.startPlayButton = null;
      this.playButton = null;
      this.pauseButton = null;

      this.addStartPlayButton();
      this.addControls();
      this.addPreviews();
      this.bindEvents();
    }

    addStartPlayButton() {
      if (this.config.autoplay || this.config.nativeControls) {
        return;
      }

      const template = this.querySelector("[data-start-play]").content;
      const playButton = document.importNode(template, true);

      this.startPlayButton = playButton.querySelector(
        "[data-start-play-button]",
      );

      this.appendChild(playButton);
    }

    addControls() {
      if (this.config.host === "youtube" || this.config.host === "vimeo") {
        return;
      }

      if (this.config.nativeControls) {
        return;
      }

      const template = this.querySelector("[data-controls]").content;
      const controls = document.importNode(template, true);

      this.playButton = controls.querySelector('[data-control="play"]');
      this.pauseButton = controls.querySelector('[data-control="pause"]');

      this.appendChild(controls);
    }

    addPreviews() {
      if (this.config.autoplay) {
        return;
      }

      if (this.config.previewImage) {
        this.startPlayButton.insertAdjacentHTML(
          "beforeend",
          this.config.previewImage,
        );

        return;
      }

      this.renderExternalPreview();
    }

    bindEvents() {
      if (this.config.previewOnly) {
        return;
      }

      this.startPlayButton?.addEventListener(
        "click",
        this.handleStartPlay.bind(this),
      );

      if (!this.config.loop) {
        this.nativeVideo?.addEventListener("ended", this.reset.bind(this));
      }

      this.playButton?.addEventListener("click", this.play.bind(this));
      this.pauseButton?.addEventListener("click", this.pause.bind(this));

      this.handleModalContext();
    }

    handleStartPlay() {
      if (this.nativeVideo) {
        this.play();
      } else {
        this.renderVideo();
      }
    }

    async renderExternalPreview() {
      if (this.config.host === "youtube") {
        this.previewImageSrc = `https://i.ytimg.com/vi/${this.config.externalID}/maxresdefault.jpg`;
      } else {
        this.previewImageSrc = await this.fetchVimeoPreviewImageSrc();
      }

      this.previewImage = document.createElement("img");
      this.previewImage.setAttribute("src", this.previewImageSrc);
      this.previewImage.setAttribute(
        "class",
        "video-player__preview-image fit-image",
      );
      this.previewImage.setAttribute("loading", "lazy");

      this.startPlayButton.append(this.previewImage);
    }

    async fetchVimeoPreviewImageSrc() {
      const response = await fetch(
        `https://vimeo.com/api/oembed.json?url=https%3A//vimeo.com/${this.config.externalID}&format=json&width=1200`,
      );
      const data = await response.json();

      return data.thumbnail_url;
    }

    renderVideo() {
      if (this.config.host === "youtube") {
        this.videoSrc = `https://www.youtube.com/embed/${this.config.externalID}`;
      } else {
        this.videoSrc = `https://player.vimeo.com/video/${this.config.externalID}`;
      }

      this.externalVideo =
        this.externalVideo || document.createElement("iframe");
      const width = this.startPlayButton.offsetWidth;
      const height = this.startPlayButton.offsetHeight;

      this.externalVideo.setAttribute("src", `${this.videoSrc}?autoplay=1`);
      this.externalVideo.setAttribute("frameborder", "0");
      this.externalVideo.setAttribute("allowfullscreen", "1");
      this.externalVideo.setAttribute("allow", "autoplay; encrypted-media");
      this.externalVideo.setAttribute("class", "video-player__video fit-image");
      this.externalVideo.setAttribute("width", width);
      this.externalVideo.setAttribute("height", height);
      this.externalVideo.setAttribute("frameborder", "0");

      this.setAttribute("ready", true);
      this.appendChild(this.externalVideo);

      this.play();
    }

    handleModalContext() {
      const modal = this.closest("[data-dialog]");

      if (modal) {
        modal.addEventListener("modalOpen", this.renderVideo.bind(this));
        modal.addEventListener("modalClose", this.reset.bind(this));
      }
    }

    play() {
      this.nativeVideo?.play();
      this.setAttribute("playback-state", "playing");
    }

    pause() {
      this.nativeVideo?.pause();
      this.setAttribute("playback-state", "paused");
    }

    reset() {
      this.externalVideo?.remove();
      this.setAttribute("playback-state", "initial");
    }
  },
);
